/**
 * iter-312i: Admin Data Health Dashboard
 *
 * Surfaces the latest run summary of every startup cleanup migration and
 * lets admins trigger a re-scan on demand. Useful for diagnosing legacy-data
 * issues without needing direct backend access.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, ShieldCheck, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminDataHealth() {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/data-health`);
      setChecks(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load data health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const rescan = async (check) => {
    setBusyId(check);
    try {
      const r = await axios.post(`${API}/admin/data-health/rescan/${check}`);
      const fixes = r.data.updated || 0;
      toast.success(
        fixes > 0
          ? `Rescan complete — ${fixes} document(s) healed`
          : `Rescan complete — data is already clean`
      );
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rescan failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-data-health-page">
        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 flex items-center gap-2">
            <ShieldCheck className="text-green-600" size={28} />
            Data Health
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Automated cleanup checks run on every backend boot to heal legacy documents.
            Re-run any check manually if you suspect new schema-drift issues.
          </p>
        </header>

        {loading && !checks.length ? (
          <div className="p-12 text-center text-stone-500 text-sm">Loading…</div>
        ) : checks.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="mx-auto text-amber-500 mb-2" size={32} />
              <p className="text-sm text-stone-600">No health checks have run yet. Restart the backend or trigger a rescan once one becomes registered.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {checks.map(c => {
              const updated = c.updated || 0;
              const scanned = c.scanned || 0;
              const isHealthy = updated === 0;
              return (
                <Card key={c.check} className="overflow-hidden" data-testid={`health-check-${c.check}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-base font-semibold text-stone-900">{c.label || c.check}</h3>
                        <code className="text-[10px] text-stone-400">{c.check}</code>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                          isHealthy ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}
                        data-testid={`health-check-status-${c.check}`}
                      >
                        {isHealthy ? 'Healthy' : `${updated} fixed`}
                      </span>
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div>
                        <dt className="text-stone-500">Last run</dt>
                        <dd className="text-stone-800 font-medium" data-testid={`health-check-last-run-${c.check}`}>
                          {formatDateTime(c.last_run_at)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-stone-500">Docs scanned</dt>
                        <dd className="text-stone-800 font-medium">{scanned}</dd>
                      </div>
                      <div>
                        <dt className="text-stone-500">Docs updated</dt>
                        <dd className={`font-medium ${updated > 0 ? 'text-amber-700' : 'text-stone-800'}`}>{updated}</dd>
                      </div>
                      {('item_fixes' in c) && (
                        <div>
                          <dt className="text-stone-500">Item fixes</dt>
                          <dd className="text-stone-800 font-medium">{c.item_fixes || 0}</dd>
                        </div>
                      )}
                      {('total_fixes' in c) && (
                        <div>
                          <dt className="text-stone-500">Total recomputes</dt>
                          <dd className="text-stone-800 font-medium">{c.total_fixes || 0}</dd>
                        </div>
                      )}
                    </dl>

                    <div className="mt-4 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rescan(c.check)}
                        disabled={busyId === c.check}
                        data-testid={`rescan-btn-${c.check}`}
                      >
                        <RefreshCw size={12} className={`mr-1 ${busyId === c.check ? 'animate-spin' : ''}`} />
                        {busyId === c.check ? 'Scanning…' : 'Re-scan'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
